/**
 * Create a contract directly under the vendor you are standing on, and move an
 * existing contract between vendors. Both are small enough to share a file.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { apiError } from './portfolioUi';

const TYPES = ['EPC', 'SUPPLY', 'SERVICE', 'CONSTRUCTION', 'CONSULTANCY', 'FRAMEWORK', 'SUBCONTRACT'];
const STATUSES = ['DRAFT', 'UNDER_REVIEW', 'ACTIVE', 'COMPLETED', 'TERMINATED'];

export const NewContractDialog = ({ open, onOpenChange, projectId, projectName, vendor, suggestedNumber, onCreated }) => {
  const [form, setForm] = useState({ contract_number: '', title: '', contract_type: 'SUPPLY',
                                     value: '', currency: 'INR', start_date: '', completion_date: '', status: 'DRAFT' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(f => ({ ...f, contract_number: suggestedNumber || '', title: '' }));
  }, [open, suggestedNumber]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.contract_number.trim() || !form.title.trim()) {
      toast.error('Contract number and title are required');
      return;
    }
    setBusy(true);
    try {
      const dates = {};
      if (form.start_date) dates.commencement_date = form.start_date;
      if (form.completion_date) dates.completion_date = form.completion_date;
      const payload = {
        project_id: projectId,
        contract_number: form.contract_number.trim(),
        title: form.title.trim(),
        contract_type: form.contract_type,
        status: form.status,
        parties: { contractor: vendor, employer: projectName },
        financials: {
          original_value: form.value ? Number(form.value) : 0,
          currency: form.currency,
        },
        ...(Object.keys(dates).length ? { dates } : {}),
      };
      const { data } = await api.post(`/contracts/projects/${projectId}/contracts`, payload);
      // The portfolio resolver reads several vendor shapes — pin it explicitly.
      await api.post(`/contracts/contracts/${data.id}/move-vendor`, { vendor });
      toast.success(`${payload.contract_number} created under ${vendor}`);
      onOpenChange(false);
      onCreated?.(data.id);
    } catch (e) {
      toast.error(apiError(e, 'Could not create the contract'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-contract-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight">New contract — {vendor}</DialogTitle>
          <DialogDescription>{projectName} · it appears under this vendor immediately; documents and invoices can be attached right after.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Contract number</Label>
            <Input value={form.contract_number} onChange={set('contract_number')} className="mt-1.5" data-testid="new-contract-number" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Title</Label>
            <Input value={form.title} onChange={set('title')} placeholder="e.g. Transformer supply package" className="mt-1.5" data-testid="new-contract-title" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Type</Label>
            <Select value={form.contract_type} onValueChange={set('contract_type')}>
              <SelectTrigger className="mt-1.5" data-testid="new-contract-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Status</Label>
            <Select value={form.status} onValueChange={set('status')}>
              <SelectTrigger className="mt-1.5" data-testid="new-contract-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Value</Label>
            <Input type="number" value={form.value} onChange={set('value')} placeholder="0" className="mt-1.5" data-testid="new-contract-value" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Currency</Label>
            <Input value={form.currency} onChange={set('currency')} className="mt-1.5" data-testid="new-contract-currency" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Start</Label>
            <Input type="date" value={form.start_date} onChange={set('start_date')} className="mt-1.5" data-testid="new-contract-start" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Contractual completion</Label>
            <Input type="date" value={form.completion_date} onChange={set('completion_date')} className="mt-1.5" data-testid="new-contract-completion" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm text-white"
                  style={{ background: 'var(--brand-accent)' }} data-testid="new-contract-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create contract
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AddVendorDialog = ({ open, onOpenChange, projectId, projectName, onAdded }) => {
  const [form, setForm] = useState({ vendor: '', category: '', contact_email: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.vendor.trim()) { toast.error('Vendor name is required'); return; }
    setBusy(true);
    try {
      await api.post(`/contracts/projects/${projectId}/vendors`, form);
      toast.success(`${form.vendor.trim()} added to ${projectName}`);
      setForm({ vendor: '', category: '', contact_email: '' });
      onOpenChange(false);
      onAdded?.(form.vendor.trim());
    } catch (e) {
      toast.error(apiError(e, 'Could not add the vendor'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-vendor-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight">Add vendor</DialogTitle>
          <DialogDescription>{projectName} · the vendor appears straight away with zero contracts, ready to receive one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Vendor name</Label>
            <Input value={form.vendor} onChange={set('vendor')} placeholder="e.g. Voltamp Transformers" className="mt-1.5" data-testid="add-vendor-name" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Category</Label>
            <Input value={form.category} onChange={set('category')} placeholder="Equipment · Civil · Services" className="mt-1.5" data-testid="add-vendor-category" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Contact email</Label>
            <Input value={form.contact_email} onChange={set('contact_email')} className="mt-1.5" data-testid="add-vendor-email" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm text-white"
                  style={{ background: 'var(--brand-accent)' }} data-testid="add-vendor-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add vendor
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MoveVendorDialog = ({ open, onOpenChange, contract, vendors = [], projectId, onMoved }) => {
  const [target, setTarget] = useState('');
  const [fresh, setFresh] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTarget(''); setFresh(''); } }, [open]);
  const options = vendors.filter(v => v && v !== contract?.vendor);

  const submit = async () => {
    const vendor = (fresh.trim() || target).trim();
    if (!vendor) { toast.error('Pick or name the vendor to move this contract to'); return; }
    setBusy(true);
    try {
      if (fresh.trim()) await api.post(`/contracts/projects/${projectId}/vendors`, { vendor });
      await api.post(`/contracts/contracts/${contract.id}/move-vendor`, { vendor });
      toast.success(`${contract.contract_number} moved to ${vendor}`);
      onOpenChange(false);
      onMoved?.(vendor);
    } catch (e) {
      toast.error(apiError(e, 'Could not move the contract'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="move-vendor-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight">Move contract to another vendor</DialogTitle>
          <DialogDescription>{contract?.contract_number} — {contract?.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-[11px] uppercase tracking-widest">Existing vendor on this project</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="mt-1.5" data-testid="move-vendor-select"><SelectValue placeholder="Choose a vendor…" /></SelectTrigger>
              <SelectContent>{options.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest">…or name a new one</Label>
            <Input value={fresh} onChange={(e) => setFresh(e.target.value)} className="mt-1.5" data-testid="move-vendor-new" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm text-white"
                  style={{ background: 'var(--brand-accent)' }} data-testid="move-vendor-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Move contract
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
